import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  FormControl, InputLabel, Select, MenuItem, OutlinedInput, Chip,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { fetchApplicationCategory, updateApplicationCategory, fetchApplicationTypes } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";

export default function ApplicationCategoryEditPage() {
  const { businessDomainId, categoryId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [original, setOriginal] = useState(null);
  const [applicationTypes, setApplicationTypes] = useState([]);
  const [selectedTypeIds, setSelectedTypeIds] = useState([]);
  const [form, setForm] = useState({
    applicationCategoryName: "",
    applicationCategoryDescription: "",
    url: "",
  });

  const loadData = useCallback(async () => {
    try {
      const [d, types] = await Promise.all([
        fetchApplicationCategory(categoryId),
        fetchApplicationTypes(businessDomainId),
      ]);
      setOriginal(d);
      setApplicationTypes(Array.isArray(types) ? types : []);
      setSelectedTypeIds(
        (d.associatedApplicationTypes || []).map((at) => at.applicationTypeId)
      );
      setForm({
        applicationCategoryName: d.applicationCategoryName || "",
        applicationCategoryDescription: d.applicationCategoryDescription || "",
        url: d.url || "",
      });
    } catch (e) { notify.apiError(e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, businessDomainId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!original) return;
    const associatedApplicationTypes = selectedTypeIds.map((id) => {
      const found = applicationTypes.find((t) => t.applicationTypeId === id);
      return { applicationTypeId: id, applicationTypeName: found?.applicationTypeName || "" };
    });
    try {
      await updateApplicationCategory(categoryId, {
        ...original,
        applicationCategoryName: form.applicationCategoryName,
        applicationCategoryDescription: form.applicationCategoryDescription || null,
        url: form.url || null,
        associatedApplicationTypes,
      });
      notify.success(t("message.update.success"));
      navigate(`/strategy/${businessDomainId}/application-categories/${categoryId}`);
    } catch (err) { notify.apiError(err); }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>{t("applicationCategory.edit.title")}</Typography>
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("applicationCategory.applicationType")}</InputLabel>
              <Select
                multiple
                value={selectedTypeIds}
                onChange={(e) => setSelectedTypeIds(e.target.value)}
                input={<OutlinedInput label={t("applicationCategory.applicationType")} />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((id) => {
                      const found = applicationTypes.find((t) => t.applicationTypeId === id);
                      return <Chip key={id} label={found?.applicationTypeName || id} size="small" />;
                    })}
                  </Box>
                )}
              >
                {applicationTypes.map((at) => (
                  <MenuItem key={at.applicationTypeId} value={at.applicationTypeId}>
                    {at.applicationTypeName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth required
              label={t("applicationCategory.name")}
              name="applicationCategoryName"
              value={form.applicationCategoryName}
              onChange={handleChange}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth
              label={t("common.url")}
              name="url"
              value={form.url}
              onChange={handleChange}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth multiline rows={3}
              label={t("common.description")}
              name="applicationCategoryDescription"
              value={form.applicationCategoryDescription}
              onChange={handleChange}
            />
          </Grid>
        </Grid>
        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit">{t("common.save")}</Button>
          <Button variant="outlined" onClick={() => navigate(`/strategy/${businessDomainId}/application-categories/${categoryId}`)}>{t("common.cancel")}</Button>
        </Box>
      </Box>
    </Box>
  );
}
